import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { DriverDemographics, DriverStatus } from '@cordic/driver-management';
import * as moment from 'moment'
import { DeviceStoreService } from 'src/app/services/device-store/device-store.service';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { SubmittableForm } from 'src/app/models/base/submittable-form.base';
import { TranslateService } from '@ngx-translate/core';
import { AccountDeleteModalComponent } from 'src/app/components/account-delete-modal/account-delete-modal.component';
import { AppRoutes } from 'src/app/app-routes';
import { LocalStorageEnum } from 'src/app/models/enum/local-storage.enum';
import { RegisterService } from 'src/app/services/register-service/register.service';
import { SaveSuccessModalComponent } from 'src/app/components/save-success-modal/save-success-modal.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';

enum FormControlNames {
  NAME = 'name',
  EMAIL = 'email',
  DOB = 'dateOfBirth',
  ADDRESS = 'address',
  POSTCODE = 'postcode',
  HOME_PHONE = 'homePhone',
  MOB_PHONE = 'mobilePhone'
}


@Component({
  selector: 'app-driver-details',
  templateUrl: './driver-details.component.html',
  styleUrls: ['./driver-details.component.scss'],
  providers: [ AccountDeleteModalComponent ]
})
export class DriverDetailsComponent extends SubmittableForm implements OnInit {

  constructor(
    translateService: TranslateService,
    private registerService: RegisterService, 
    private formBuilder: FormBuilder, 
    private deviceStore: DeviceStoreService,
    private router: Router,
    public modalService: NgbModal,
    private spinner: NgxSpinnerService,
  ) 
  {
    super(translateService)
  }

  email: string = '';
  mobileTelNo: string = '';

  formControlNames = FormControlNames;

  canDeleteDriver: boolean = false;

  formIsLoaded: boolean = false;

  faBack = faArrowLeft;

  async ngOnInit(): Promise<void>
  {
    this.spinner.show();

    this.form = this.formBuilder.group({
      name: new FormControl(null, {
        validators: [Validators.required]
      }),
      email: new FormControl(null, {
        validators: [Validators.email, Validators.required]
      }),
      dateOfBirth: new FormControl(null, {
        validators: [Validators.required]
      }),
      address: new FormControl(null, {
        validators: [Validators.required]
      }),
      postcode: new FormControl(null, {
        validators: [Validators.required]
      }),
      homePhone: new FormControl(null, {
        validators: [Validators.required, Validators.minLength(6)]
      }),
      mobilePhone: new FormControl(null, {
        validators: [Validators.required, Validators.minLength(6)]
      }),
    });
  
    this.autoCapitalizeWords([this.form.controls[FormControlNames.NAME]]);

  
    await firstValueFrom(this.registerService.userDemographics).then((user) =>
      {
        if (user)
        {
          this.configureForm(user);
        }
      });

    this.deviceStore.getSession().subscribe((session) => {
      if (session.canDeleteDriver)
      {
        this.canDeleteDriver = session.canDeleteDriver;
      }
    });

    this.formIsLoaded = true;

    this.spinner.hide();
  }

  shouldShowPage(): boolean
  {
    return this.formIsLoaded;
  }

  configureForm(personalInfo: DriverDemographics) 
  {
    if(this.form)
    {
      if (personalInfo.fullname) {
        this.form.patchValue({
          name: personalInfo.fullname,
        });
      }
      if (personalInfo.email) {
        this.form.patchValue({
          email: personalInfo.email
        });
        this.email = personalInfo.email;
      }
      if (personalInfo.dob) {
        this.form.patchValue({
          dateOfBirth: moment(personalInfo.dob, 'L').format('YYYY-MM-DD')
        });
      }
      if (personalInfo.address) {
        this.form.patchValue({
          address: personalInfo.address
        });
      }
      if (personalInfo.postcode) {
        this.form.patchValue({
          postcode: personalInfo.postcode
        });
      }
      if (personalInfo.homeTelNo) {
        this.form.patchValue({
          homePhone: personalInfo.homeTelNo
        });
      }
      if (personalInfo.mobileTelNo) {
        this.form.patchValue({
          mobilePhone: personalInfo.mobileTelNo
        });
        this.mobileTelNo = personalInfo.mobileTelNo;
      }
    }
  }

  getLabelName(controlName: string) : string
  {
    let labelName: string = '';

    switch(controlName)
    {
      case this.formControlNames.ADDRESS:
        labelName = this.translateService.instant('IDS_DD_ADDRESS');
        break;
      case this.formControlNames.EMAIL:
        labelName = this.translateService.instant('IDS_DD_EMAIL');
        break;
      case this.formControlNames.DOB:
        labelName = this.translateService.instant('IDS_DD_DOB');
        break;
      case this.formControlNames.NAME:
        labelName = this.translateService.instant('IDS_DD_NAME');
        break;
      case this.formControlNames.POSTCODE:
        labelName = this.translateService.instant('IDS_DD_POSTCODE');
        break;
      case this.formControlNames.MOB_PHONE:
        labelName = this.translateService.instant('IDS_DD_MOBILE_TEL');
        break;
      case this.formControlNames.HOME_PHONE:
        labelName = this.translateService.instant('IDS_DD_HOME_TEL');
        break;
    }
    
    return labelName;
  }

  getInputType(controlName: string): string
  {
    let inputType: string = '';

    switch(controlName)
    {
      case this.formControlNames.NAME:
      case this.formControlNames.ADDRESS:
      case this.formControlNames.POSTCODE:
        inputType = this.translateService.instant('IDS_DD_INPUT_TEXT');
        break;
      case this.formControlNames.EMAIL:
        inputType = this.translateService.instant('IDS_DD_INPUT_EMAIL');
        break;
      case this.formControlNames.DOB:
        inputType = this.translateService.instant('IDS_DD_INPUT_DATE');
        break;
      case this.formControlNames.MOB_PHONE:
      case this.formControlNames.HOME_PHONE:
        inputType = this.translateService.instant('IDS_DD_INPUT_TEL');
        break;
    }
    
    return inputType;
  }

  getFormControls() : string[]
  {
    if (this.form)
    {
      return Object.keys(this.form.controls);
    }
    else
    {
      return [];
    }
  }

  getControlValue(control: string) : string
  {
    if ((this.form) && 
        (this.form.controls[control]))
    {
      return this.form.controls[control].value;
    }
    else
    {
      return '';
    }
  }

  getElementTagName(control: string): string
  {
    return this.getLabelName(control).toLowerCase();
  }

  returnToLogin()
  {
    this.router.navigate([AppRoutes.Login]);

    // Make sure localStorage is cleared on logout
    localStorage.removeItem(LocalStorageEnum.CAN_DEL_DRIVER);

    localStorage.removeItem(LocalStorageEnum.USER);

    localStorage.removeItem(LocalStorageEnum.JWT);
  }

  shouldShowDeleteAccount() : boolean
  {
    return this.canDeleteDriver;
  }

  openAccountDelModal()
  {
    this.modalService.open(AccountDeleteModalComponent, {backdrop: 'static', centered: true, });
  }

  requiresConfirmation(): boolean
  {
    return (this.form?.value.mobilePhone !== this.mobileTelNo);
  }

  updatePersonalInfo() {
    this.spinner.show();

    return super.onConfirm(
      () => this.registerService.sendDetails({
        fullname: this.form?.value.name,
        dob: moment(this.form?.value.dateOfBirth).format('L'),
        address: this.form?.value.address,
        email: this.form?.value.email,
        postcode: this.form?.value.postcode,
        homeTelNo: this.form?.value.homePhone,
        mobileTelNo: this.form?.value.mobilePhone,
      }),
      {
        onSuccess: () => {
          this.spinner.hide();

          this.form?.markAsPristine();
          
          if (this.form?.value.name && !this.form.controls[FormControlNames.NAME].pristine) {
            this.deviceStore.setUser({ name: this.form.value.name });
          }
          
          if (this.requiresConfirmation())
          {
            this.deviceStore.setSession({ driverStatus: DriverStatus.REGISTERED });
            this.router.navigate([AppRoutes.ConfirmUpdate]);
          }
          else 
          {
            // Open Modal to show successful save
            this.modalService.open(SaveSuccessModalComponent, { centered: true });
            
            // Close modal after 1sec
            setTimeout(() => {
              this.modalService.dismissAll();
            }, 2000)
          }
        },
        onError: () => {
          this.spinner.hide();
        }
      }
    );
  }

  getIsFormChanged(): boolean
  {
    return this.form ? this.form.pristine : false;
  }

  getIsFormValid(): boolean
  {
    return this.form ? this.form.valid : false;
  }

  getIsControlValid(control: string)
  {
    if ((this.form) && 
        (this.form.controls[control]))
    {
      return this.form.controls[control].valid;
    }
    else
    {
      return false;
    }
  }
}


